import { Button } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { useClassificationBootstrap, useMergeValue, useValueWithAxis } from '@/hooks/useClassification'
import { MergeValueDialog } from './MergeValueDialog'

/**
 * The merge control as it appears on a brand or product page: one button that opens the
 * dialog already knowing which value the page is about.
 *
 * The page holds a `valueId` but no `axisId` — a brand page's DTO has no reason to carry
 * one — so both the axis and the sibling values it could merge into come out of the
 * bootstrap registry every session already has cached. Deliberately NOT `useValues`: the
 * product page eliminated its per-axis reads down to that one bootstrap call, and a merge
 * button is not a reason to put eighteen of them back.
 */
export interface MergeValueActionProps {
	/** The value this page is about: the one that would be retired. */
	valueId: string
	code: string
	label: string
	/** Where to send the reader once its page has stopped existing. */
	onMerged?: () => void
}

export function MergeValueAction({ valueId, code, label, onMerged }: MergeValueActionProps) {
	const { data: bootstrap } = useClassificationBootstrap()
	const resolved = useValueWithAxis(valueId)
	const axisId = resolved?.axis?.axisId ?? ''
	// Keyed by axis CODE, not id: `valuesByAxis` is a code-keyed map, and indexing it by
	// id yields nothing — which is what disabled the button.
	const axisCode = resolved?.axis?.code
	// Its siblings are the rest of its own axis — the only values a merge can legally target.
	const values = useMemo(() => (axisCode ? (bootstrap?.valuesByAxis?.[axisCode] ?? []) : []), [bootstrap, axisCode])
	const mergeValue = useMergeValue(axisId)
	const [open, setOpen] = useState(false)

	const confirm = (into: string) => {
		mergeValue.mutate(
			{ valueId, into },
			{
				onSettled: () => setOpen(false),
				// Only on success: a failed merge leaves the page's subject alive, and
				// navigating away from it would hide the error the user has to act on.
				onSuccess: () => onMerged?.(),
			},
		)
	}

	return (
		<>
			<Button
				type="button"
				variant="secondary"
				className="rounded-ds-full"
				// Until the registry lands there is no axis to merge within, so the control
				// waits rather than opening a dialog with nothing to choose from.
				isDisabled={!axisId}
				onPress={() => setOpen(true)}
			>
				Merge into another…
			</Button>
			<MergeValueDialog
				open={open}
				subject={{ valueId, code, label }}
				candidates={values.map((v) => ({ valueId: v.valueId, code: v.code, label: v.label }))}
				submitting={mergeValue.isPending}
				onConfirm={confirm}
				onCancel={() => setOpen(false)}
			/>
		</>
	)
}
